import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Result } from 'ts-results'

import { Form } from 'app/common/presentation'
import { ArrowUpRightIcon, CheckmarkIcon, ErrorIcon } from 'app/common/presentation/icons'
import { createTrackingScript } from 'app/connections/application/innkeepr'
import {
  InnkeeprConnection,
  InstalledInnkeeprConnection,
  isInstalledConnection,
} from 'app/connections/types'
import useWebSocket from 'app/events/presentation/useWebSockets'
import { Event, EventMessage } from 'app/events/types'
import { useUser } from 'app/user/application'

import { Badge, Button, Code, Input, Link, Switch } from 'components'
import { CodeFunctions } from 'components/Code'

import { SettingsProps, noSaveIfInstalled } from '../Settings'
import setupStyles from '../Setup.module.scss'
import styles from './InnkeeprSettings.module.scss'

interface InnkeeprSettingsProps extends SettingsProps {
  isSaving: boolean
  onSave: () => Promise<Result<void, string | void>>
  onTrackingUrlUpdate: (trackingUrl: string) => void
  connection: InnkeeprConnection | InstalledInnkeeprConnection
  trackingUrl: string
}

type Environment = 'development' | 'production'
type VerificationState = 'error' | 'success'

const trackingUrlKey = 'innkeepr_tracking_url'

const InnkeeprSettings = ({
  isSaving,
  onSave,
  onConnectionConfigValidationUpdate,
  onTrackingUrlUpdate,
  connection,
  trackingUrl: incomingTrackingUrl,
}: InnkeeprSettingsProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('connections')

  const { workspace } = useUser()

  const [trackingUrl, setTrackingUrl] = useState(
    (incomingTrackingUrl || localStorage.getItem(trackingUrlKey)) ?? '',
  )

  const isInstalled = isInstalledConnection(connection)

  const [environment, setEnvironment] = useState<Environment>(
    isInstalled ? 'production' : 'development',
  )
  const [events, setEvents] = useState<Event[]>([])
  const [verificationState, setVerificationState] = useState<VerificationState>()

  const codeRef = useRef<CodeFunctions>(null)

  // save tracking url until connection is saved to survive reloads and page navigations
  useEffect(() => {
    if (isInstalled) {
      localStorage.removeItem(trackingUrlKey)
    } else {
      localStorage.setItem(trackingUrlKey, trackingUrl)
    }
  }, [isInstalled, trackingUrl])

  useEffect(() => onTrackingUrlUpdate(trackingUrl), [onTrackingUrlUpdate, trackingUrl])

  const navigateToEvents = () => void navigate('/events')

  const handleMessage = useCallback((message: string) => {
    const eventMessage: EventMessage = JSON.parse(message) as EventMessage

    switch (eventMessage.type) {
      case 'existing_events':
        setEvents(eventMessage.events)
        break
      case 'new_event':
        setEvents((prev) => (prev ? [eventMessage.event, ...prev] : [eventMessage.event]))
        break
    }
  }, [])

  useWebSocket('/events', handleMessage)

  const showActionButton = () => {
    if (
      isInstalledConnection(connection) &&
      noSaveIfInstalled.includes(connection.name) &&
      connection.status !== 'draft'
    ) {
      return false
    }

    return true
  }

  return (
    <div className={styles.innkeepr}>
      {isInstalled ? (
        <>
          <div className={styles.step}>
            <Badge mint value={1} />
            <div className={styles.section}>
              <div className={styles.header}>
                <h2>{t('setup.innkeepr.steps.1.title')}</h2>
                <div className={styles.description}>{t('setup.innkeepr.steps.1.description')}</div>
              </div>

              <Switch
                checked={environment === 'production'}
                labelChecked={t('setup.innkeepr.environment.production')}
                labelUnchecked={t('setup.innkeepr.environment.development')}
                name={'environment'}
                onChange={(checked) => setEnvironment(checked ? 'development' : 'production')}
              />
              <div className={styles.codeContainer}>
                <div>{t('setup.innkeepr.steps.1.thenCopyCode')}</div>
                {workspace && (
                  <Code
                    className={styles.code}
                    code={createTrackingScript(
                      workspace.apiKey,
                      trackingUrl,
                      environment === 'development',
                    )}
                    language={'javascript'}
                    minify
                    ref={codeRef}
                    what={'Script'}
                  />
                )}
              </div>
              <Button variant="secondary" onClick={() => codeRef.current?.copyCode()}>
                {t('setup.innkeepr.steps.1.copyCode')}
              </Button>
            </div>
          </div>
          <div className={styles.step}>
            <Badge mint value={2} />
            <div className={styles.section}>
              <div className={styles.header}>
                <h2>{t('setup.innkeepr.steps.2.title')}</h2>
                <div className={styles.description}>{t('setup.innkeepr.steps.2.description')}</div>
              </div>
              <Link
                title={t('setup.innkeepr.steps.2.info.title')}
                to={t('setup.innkeepr.steps.2.info.url')}
              />
            </div>
          </div>
          <div className={styles.step}>
            <Badge mint value={3} />
            <div className={styles.section}>
              <div className={styles.header}>
                <h2>{t('setup.innkeepr.steps.3.title')}</h2>
                <div className={styles.description}>
                  <Trans
                    components={{
                      a: <a />,
                      a1: <a onClick={navigateToEvents} />,
                      br: <br />,
                    }}
                    values={{ docuUrl: t('setup.innkeepr.steps.3.docuUrl') }}
                    i18nKey={`setup.innkeepr.steps.3.description`}
                    ns="connections"
                  />
                </div>
              </div>
              <Button
                onClick={navigateToEvents}
                rightIcon={<ArrowUpRightIcon />}
                variant="secondary"
              >
                {t('setup.innkeepr.steps.3.testEvents')}
              </Button>
            </div>
          </div>
          <div className={styles.step}>
            <Badge mint value={4} />
            <div className={styles.section}>
              <div className={styles.header}>
                <h2>{t('setup.innkeepr.steps.4.title')}</h2>
                <div className={styles.description}>{t('setup.innkeepr.steps.4.description')}</div>
              </div>
              <div className={styles.verify}>
                {verificationState === 'success' ? (
                  <div className={styles.success}>
                    <CheckmarkIcon />
                    {t(`setup.innkeepr.steps.4.verify.success`)}
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        if (events.length) {
                          onConnectionConfigValidationUpdate(true)
                          setVerificationState('success')
                        } else {
                          setVerificationState('error')
                        }
                      }}
                      variant="secondary"
                    >
                      {t('setup.innkeepr.steps.4.verify.action')}
                    </Button>
                    {verificationState === 'error' && (
                      <div className={styles.error}>
                        <ErrorIcon />
                        {t(`setup.innkeepr.steps.4.verify.error`)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <Form.FormRow
          className={styles.formRow}
          description={t('setup.innkeepr.config.trackingUrl.description')}
          title={t('setup.innkeepr.config.trackingUrl.title')}
        >
          <Input
            className={styles.trackingUrlInput}
            onChange={isInstalled ? undefined : setTrackingUrl}
            placeholder={t('setup.innkeepr.config.trackingUrl.placeholder')}
            value={trackingUrl}
          />
        </Form.FormRow>
      )}
      <div className={setupStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        {showActionButton() && (
          <Button
            disabled={isInstalled && verificationState !== 'success'}
            loading={isSaving}
            onClick={(e) => {
              e.stopPropagation()

              void onSave()
            }}
          >
            {t(isInstalled ? 'setup.actions.save' : 'setup.actions.next')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default InnkeeprSettings
